import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { toast } from "react-toastify";
import CertificateCanvas from "../certificatePrint/CertificateCanvas";
import CertificateToolbar from "../certificatePrint/CertificateToolbar";
import {
  CertificatePageSettings,
  getCssAtPageSize,
  getPageSize,
} from "../certificatePrint/certificateTypes";
import { useLoggedInClinic } from "../certificatePrint/useLoggedInClinic";
import { resolveCertificateLogo } from "../certificatePrint/getLoggedInClinic";
import "../certificatePrint/certificatePrint.css";
import NewBillSettingsPanel from "./NewBillSettingsPanel";
import {
  mergeClinicPreviewData,
  NEW_BILL_SAMPLE_DATA,
  NEW_BILL_SAMPLE_TABLES,
} from "./newBillSampleData";
import {
  cloneNewBillTemplate,
} from "./newBillTemplates";
import {
  createNewBillTemplateId,
  downloadNewBillPdf,
  findNewBillTemplate,
  getDefaultNewBillBuiltin,
  hydrateNewBillStore,
  loadNewBillStore,
  printNewBillViaPdf,
  saveNewBillStore,
} from "./newBillStorage";
import { InvoicePrintDefault } from "./newBillTypes";
import baseUrl from "../../../../api/baseUrl";
import { isPrintTemplate2Enabled } from "../../../../utils/clinicFeatures";

function waitForPaint() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

function normalizeDefault(value: unknown, allowTemplate2: boolean): InvoicePrintDefault {
  if (value === "newBillPrint") return "newBillPrint";
  if (value === "printTemplate_2" && allowTemplate2) return "printTemplate_2";
  return "bill";
}

function NewBillDesigner() {
  const clinic = useLoggedInClinic();
  const stageRef = useRef<HTMLDivElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);
  const printTemplate2Enabled = isPrintTemplate2Enabled();

  const [store, setStore] = useState(loadNewBillStore);
  const storeRef = useRef(store);
  storeRef.current = store;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(0.75);
  const [mobilePane, setMobilePane] = useState<"design" | "preview">("preview");
  const [printing, setPrinting] = useState(false);
  const [invoicePrintDefault, setInvoicePrintDefault] =
    useState<InvoicePrintDefault>("bill");
  const invoicePrintDefaultRef = useRef(invoicePrintDefault);
  invoicePrintDefaultRef.current = invoicePrintDefault;

  const active = findNewBillTemplate(store.templates, store.activeId);

  useEffect(() => {
    saveNewBillStore(store);
  }, [store]);

  useEffect(() => {
    baseUrl
      .get("/clinic/getBillData")
      .then((response) => {
        const data = response?.data?.data;
        const saved = data?.newBillPrint;
        if (saved) setStore(hydrateNewBillStore(saved));
        setInvoicePrintDefault(
          normalizeDefault(data?.invoicePrintDefault, printTemplate2Enabled)
        );
      })
      .catch(() => {
        /* keep local store */
      });
  }, [printTemplate2Enabled]);

  const previewPage = useMemo(
    () => ({
      ...active.page,
      logoSrc: resolveCertificateLogo(clinic.printImages, active.page.logoSrc),
      letterheadSrc: active.page.letterheadSrc || "",
    }),
    [active.page, clinic.printImages]
  );

  const previewData = useMemo(
    () =>
      mergeClinicPreviewData(NEW_BILL_SAMPLE_DATA, {
        clinicName: clinic.clinicName,
        address: clinic.address,
        tpNo: clinic.tpNo,
        drName: clinic.drName,
        doctorDesignation: clinic.doctorDesignation,
      }),
    [clinic]
  );

  const updateActive = useCallback(
    (updater: (template: typeof active) => typeof active) => {
      setStore((current) => ({
        ...current,
        templates: current.templates.map((template) =>
          template.id === current.activeId ? updater(template) : template
        ),
      }));
    },
    []
  );

  const persistToClinic = async (
    nextStore = storeRef.current,
    nextDefault = invoicePrintDefaultRef.current
  ) => {
    await baseUrl.patch("/clinic/saveNewBillPrint", {
      newBillPrint: nextStore,
      invoicePrintDefault: nextDefault,
    });
  };

  const fitToScreen = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const size = getPageSize(active.page.orientation, active.page.size);
    const available = stage.clientWidth - 48;
    const pagePx = (size.widthMm * 96) / 25.4;
    setZoom(Math.max(0.25, Math.min(1.2, available / pagePx)));
  }, [active.page.orientation, active.page.size]);

  const selected = active.elements.find((item) => item.id === selectedId) ?? null;

  const handlePrint = async () => {
    const paper = paperRef.current;
    if (!paper) return;
    const previousZoom = zoom;
    setSelectedId(null);
    setPrinting(true);
    setZoom(1);
    await waitForPaint();
    try {
      // Same PDF builder as Download PDF so the print dialog matches the template.
      await printNewBillViaPdf({
        stack: paper,
        size: active.page.size,
        orientation: active.page.orientation,
      });
    } catch {
      toast.error("Could not prepare the print preview.");
    } finally {
      setZoom(previousZoom);
      setPrinting(false);
    }
  };

  const handleDownloadPdf = async () => {
    const paper = paperRef.current;
    if (!paper) return;
    const previousZoom = zoom;
    setSelectedId(null);
    setPrinting(true);
    setZoom(1);
    await waitForPaint();
    try {
      await downloadNewBillPdf({
        stack: paper,
        size: active.page.size,
        orientation: active.page.orientation,
        filename: `${active.name.replace(/\s+/g, "-").toLowerCase()}.pdf`,
      });
    } catch {
      toast.error("Could not prepare the PDF preview.");
    } finally {
      setZoom(previousZoom);
      setPrinting(false);
    }
  };

  return (
    <div className="certificate-designer">
      <style>
        {`@media print { @page { size: ${getCssAtPageSize(active.page.orientation, active.page.size)}; margin: 0; } }`}
      </style>
      <div className="certificate-mobile-tabs" data-no-print="true">
        <button
          type="button"
          className={mobilePane === "design" ? "is-active" : ""}
          onClick={() => setMobilePane("design")}
        >
          Design
        </button>
        <button
          type="button"
          className={mobilePane === "preview" ? "is-active" : ""}
          onClick={() => setMobilePane("preview")}
        >
          Preview
        </button>
      </div>

      <div className={`certificate-designer-grid is-${mobilePane}`}>
        <aside className="certificate-panel certificate-controls" data-no-print="true">
          <div className="certificate-panel-header">
            <span className="print-panel-eyebrow">Design controls</span>
            <h2>New bill designer</h2>
            <p>
              Customise the live bill preview. Placeholders stay bound to sample data until an
              invoice is printed.
            </p>
          </div>
          <NewBillSettingsPanel
            templates={store.templates}
            activeId={store.activeId}
            defaultId={store.defaultId}
            page={active.page}
            elements={active.elements}
            selected={selected}
            invoicePrintDefault={invoicePrintDefault}
            onInvoicePrintDefaultChange={setInvoicePrintDefault}
            onSelectTemplate={(id) => {
              setStore((current) => ({ ...current, activeId: id }));
              setSelectedId(null);
            }}
            onRename={(name) => updateActive((template) => ({ ...template, name }))}
            onSaveAsNew={() => {
              const copy = cloneNewBillTemplate(active, {
                id: createNewBillTemplateId(),
                name: `${active.name} copy`,
              });
              setStore((current) => ({
                ...current,
                templates: [...current.templates, copy],
                activeId: copy.id,
              }));
              toast.success("Created a new template. Click Save template to store it for this clinic.");
            }}
            onDuplicate={() => {
              const copy = cloneNewBillTemplate(active, {
                id: createNewBillTemplateId(),
                name: `${active.name} copy`,
              });
              setStore((current) => ({
                ...current,
                templates: [...current.templates, copy],
                activeId: copy.id,
              }));
              toast.success("Template duplicated.");
            }}
            onReset={() => {
              const builtin = getDefaultNewBillBuiltin(active.id);
              updateActive(() => ({
                ...builtin,
                id: active.id,
                name: active.name,
              }));
              toast.info("Reset to the built-in layout for this template.");
            }}
            onSetDefault={() => {
              setStore((current) => ({ ...current, defaultId: current.activeId }));
              toast.success("Set as the default New Bill Print template.");
            }}
            onPageChange={(patch: Partial<CertificatePageSettings>) =>
              updateActive((template) => {
                const prevPage = template.page;
                const nextPage = { ...prevPage, ...patch };

                const touchesTypography =
                  patch.fontFamily !== undefined ||
                  patch.fontSize !== undefined ||
                  patch.fontWeight !== undefined ||
                  patch.lineHeight !== undefined ||
                  patch.italic !== undefined ||
                  patch.underline !== undefined;

                if (!touchesTypography) {
                  return { ...template, page: nextPage };
                }

                const sizeRatio =
                  patch.fontSize != null && Number(prevPage.fontSize) > 0
                    ? Number(patch.fontSize) / Number(prevPage.fontSize)
                    : 1;

                return {
                  ...template,
                  page: nextPage,
                  elements: template.elements.map((item) => {
                    if (item.type === "logo") {
                      return patch.fontFamily != null
                        ? { ...item, fontFamily: patch.fontFamily }
                        : item;
                    }

                    const next = { ...item };
                    if (patch.fontFamily != null) {
                      next.fontFamily = patch.fontFamily;
                    }
                    if (patch.fontSize != null && sizeRatio !== 1) {
                      next.fontSize = Math.max(
                        6,
                        Math.round(item.fontSize * sizeRatio * 10) / 10
                      );
                    }
                    if (patch.lineHeight != null && item.type === "text") {
                      next.lineHeight = patch.lineHeight;
                    }
                    if (patch.fontWeight != null && item.type === "text") {
                      if (item.fontWeight < 700) {
                        next.fontWeight = patch.fontWeight;
                      }
                    }
                    if (patch.italic != null) {
                      next.italic = patch.italic;
                    }
                    if (patch.underline != null) {
                      next.underline = patch.underline;
                    }
                    return next;
                  }),
                };
              })
            }
            onElementChange={(id, patch) =>
              updateActive((template) => ({
                ...template,
                elements: template.elements.map((item) =>
                  item.id === id ? { ...item, ...patch } : item
                ),
              }))
            }
            onToggleField={(ids, visible) =>
              updateActive((template) => ({
                ...template,
                elements: template.elements.map((item) =>
                  ids.includes(item.id) ? { ...item, visible } : item
                ),
              }))
            }
            onSelectElement={setSelectedId}
            onSave={() => {
              const nextStore = {
                ...storeRef.current,
                defaultId: storeRef.current.activeId,
              };
              setStore(nextStore);
              persistToClinic(nextStore, invoicePrintDefaultRef.current)
                .then(() => {
                  try {
                    localStorage.setItem(
                      "invoicePrintTemplate",
                      invoicePrintDefaultRef.current
                    );
                  } catch {
                    /* ignore */
                  }
                  window.dispatchEvent(
                    new CustomEvent("invoicePrintDefaultChanged", {
                      detail: {
                        invoicePrintDefault: invoicePrintDefaultRef.current,
                      },
                    })
                  );
                  window.dispatchEvent(
                    new CustomEvent("newBillPrintRefresh", {
                      detail: { store: nextStore },
                    })
                  );
                  toast.success("Template and default print engine saved for this clinic.");
                })
                .catch(() => toast.error("Could not save the bill template."));
            }}
          />
        </aside>

        <section className="certificate-panel certificate-preview">
          <div className="certificate-panel-header" data-no-print="true">
            <span className="print-panel-eyebrow">New bill print preview</span>
            <h2>
              {active.page.size} · {active.page.orientation}
            </h2>
            <p>Zoom is editor-only. Print uses the real page without controls or guides.</p>
          </div>
          <div className="certificate-preview-body">
            <div data-no-print="true">
              <CertificateToolbar zoom={zoom} onZoomChange={setZoom} onFit={fitToScreen} />
            </div>
            <div className="certificate-stage" ref={stageRef}>
              <CertificateCanvas
                page={previewPage}
                elements={active.elements}
                selectedId={printing ? null : selectedId}
                previewData={previewData}
                previewTables={NEW_BILL_SAMPLE_TABLES}
                interactive={!printing}
                zoom={printing ? 1 : zoom}
                paperRef={paperRef}
                onSelect={setSelectedId}
                onElementsChange={(updater) =>
                  updateActive((template) => ({
                    ...template,
                    elements: updater(template.elements),
                  }))
                }
              />
            </div>
            <div className="certificate-footer-bar mt-4" data-no-print="true">
              <button type="button" className="certificate-btn" onClick={handleDownloadPdf}>
                <PictureAsPdfIcon sx={{ fontSize: 16 }} /> Download PDF
              </button>
              <button type="button" className="certificate-btn" onClick={handlePrint}>
                <PrintIcon sx={{ fontSize: 16 }} /> Print
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default NewBillDesigner;
